import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { COUNTRY_ENUM, FollowerTierOption, PLATFORM_ENUM } from 'services/api/constants'
import { useSelector } from 'react-redux'
import { makeFollowerTierOptionsByPlatformSelector } from 'state/constants'
import { useField, useFormikContext } from 'formik'
import { selectEditingCampaignCountry } from 'state/campaigns'
import { abbreviateNumber } from 'services/numbers'
import RangeInput, { OptionsState } from 'particles/RangeInput'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'
import { fieldIdentifiers } from 'components/Shared/Comment'
import { RequirementCardContainer } from 'components/Shared/Fields/styles'

interface FollowerRequirementProps {
    name: string
    platform: PLATFORM_ENUM
    onRemove: () => void
}

const FollowerRequirement: React.FC<FollowerRequirementProps> = ({ name, platform, onRemove }) => {
    const [t] = useTranslation()
    const { setFieldValue } = useFormikContext()
    const [field, meta] = useField<any>(name)
    const country = useSelector(selectEditingCampaignCountry)
    const followerTierOptionsSelector = useMemo(() => makeFollowerTierOptionsByPlatformSelector(platform), [platform])
    const options = useSelector(followerTierOptionsSelector)
    const requirementUuid = field.value.uuid
    const hasError = Boolean(meta.touched && meta.error)

    const sortList = (
        list: FollowerTierOption[],
        sortType: 'min' | 'max',
        country: COUNTRY_ENUM | null,
    ): OptionsState[] => {
        const initLabel = sortType === 'min' ? t('placeholderText.noMinimum') : t('placeholderText.noMaximum')
        const defaultOptions: OptionsState[] = [{ label: initLabel, value: null }]
        list.forEach((option: FollowerTierOption) => {
            const label =
                country && abbreviateNumber(option.min, country) ? abbreviateNumber(option.min, country) : option.min
            if (option.min === 0) return
            const returnObject = {
                label: String(label),
                value: option.min,
            }
            if (sortType === 'min') {
                defaultOptions.push(returnObject)
            } else {
                defaultOptions.unshift(returnObject)
            }
        })
        return defaultOptions
    }
    const Options = useMemo(() => {
        const returnObjects = {}
        const minOptions = sortList(options, 'min', country)
        const maxOptions = sortList(options, 'max', country)
        return { ...returnObjects, min: minOptions, max: maxOptions }
    }, [options, country, t])

    const followerTitleAndSubtitle: { [key: number]: string } = {
        [PLATFORM_ENUM.IG]: t('socialMedia.requirement.instagramFollowers'),
        [PLATFORM_ENUM.FB]: t('socialMedia.requirement.facebookFriends'),
        [PLATFORM_ENUM.TWITTER]: t('socialMedia.requirement.twitterFollowers'),
        [PLATFORM_ENUM.TIKTOK]: t('socialMedia.requirement.tiktokFollowers'),
        [PLATFORM_ENUM.XIAOHONGSHU]: t('campaignRequirement.XHSFollowersHeader'),
    }

    const selectedValue = (sortType: 'min' | 'max'): OptionsState | null => {
        if (Options && Options.min && Options.max) {
            let returnValue
            if (sortType === 'min') {
                returnValue = Options.min.find(option => field.value && field.value.min === option.value)
            } else {
                returnValue = Options.max.find(option => field.value && field.value.max === option.value)
            }
            if (!returnValue) {
                return null
            } else {
                return returnValue.value
            }
        } else {
            return null
        }
    }

    if (!country) return null

    return (
        <CampaignEditorInputFieldContainer>
            <RequirementCardContainer isCard>
                <RangeInput.Dropdown
                    label={followerTitleAndSubtitle[platform]}
                    subtext={t('campaignEditor.requirement.instagramFollowerSubtext')}
                    isRequired
                    firstRangeTitle={
                        platform === PLATFORM_ENUM.FB
                            ? t('campaignEditor.customAndRegistrationField.enterMinimumNumberHeader')
                            : t('campaignEditor.requirement.enterInstagramMinimumFollower')
                    }
                    firstRangeId={`follower-platform-${platform}-min`}
                    firstRangeOptions={Options.min}
                    firstRangeValue={selectedValue('min') ? selectedValue('min') : null}
                    firstRangePlaceholder={t('placeholderText.noMinimum')}
                    firstRangeOnChange={(option): void => setFieldValue(`${name}.min` as never, option ? option : null)}
                    firstRangeIdentifier={`${requirementUuid}_${platform}_${fieldIdentifiers.min}`}
                    secondRangeTitle={
                        platform === PLATFORM_ENUM.FB
                            ? t('campaignEditor.customAndRegistrationField.enterMaximumNumberHeader')
                            : t('campaignEditor.requirement.enterInstagramMaximumFollower')
                    }
                    secondRangeValue={selectedValue('max') ? selectedValue('max') : null}
                    secondRangeId={`follower-platform-${platform}-max`}
                    secondRangeOptions={Options.max}
                    secondRangeOnChange={(option): void =>
                        setFieldValue(`${name}.max` as never, option ? option : null)
                    }
                    secondRangePlaceholder={t('placeholderText.noMaximum')}
                    secondRangeIdentifier={`${requirementUuid}_${platform}_${fieldIdentifiers.max}`}
                    width="100%"
                    onClose={onRemove}
                    errorMessage={hasError ? meta.error : ''}
                />
            </RequirementCardContainer>
        </CampaignEditorInputFieldContainer>
    )
}

export default FollowerRequirement
